// ── useConfirm — the questions of the app (#665, F64) ───────────────────────
// `const { confirm, askText } = useConfirm()` replaces the native confirm()
// and prompt(). A native dialog cannot show the shared modal parts, a phone
// browser can block it, and an e2e spec must answer it outside the page.
//
//   if (!(await confirm({ title, message, confirmLabel }))) return;
//   const text = await askText({ message, initialValue });  // null = cancel
//
// The rules (operator decision on #665, 2026-10-28):
// - ONE ConfirmModal shows the questions. A second question waits in a queue
//   (first in, first out).
// - Esc, the close X and a click on the backdrop answer no (null for
//   askText). A change of the page (a new path) and an unmount of the
//   provider answer no to the open question and to each waiting question.
// - A question belongs to the page that asked it. useConfirm() binds its
//   functions to the path of the render that made them. A question from an
//   old path (a reply of the network that came after the person left the
//   page) gets no at once and does not show (#665 review). A tab change
//   keeps the path (?tab=): a tab that asks after an await checks its own
//   mount (WeighinTab).
// - A question that is the same as an open or a waiting question gets no at
//   once: a double tap on a button asks one time.
// - Each question keeps its confirm button off for QUESTION_ARM_MS after it
//   opens: a fast double click on the button that asks cannot land on the
//   confirm button (#665 review). A question that opens less than
//   SECOND_QUESTION_ARM_MS after an answer (the second question of a hard
//   delete, a question from the queue) keeps it off for that longer time. A
//   double click on the first question then cannot answer the second one
//   (#641, #661).
// - The first focus (ConfirmModal): a plain question focuses its confirm
//   button, so Enter answers yes, as the native confirm() did. A destructive
//   question has a red confirm button and focuses Cancel, so Enter answers
//   no. A question in the longer pause also focuses Cancel. A text question
//   focuses its field.
// - A held Enter (key repeat) answers nothing: only the first press does.
// - A question is never asked from a layout effect: the page binding moves in
//   the layout effect of the provider, and a question from a layout effect of
//   the new page could meet the old path.
// - Ask BEFORE run() of useBusy(), never inside it: a question inside run()
//   holds the lock of #605 while the person reads. The one exception is a
//   question that a refusal of the server starts (BoutCard, ROUND_FEW_CARDS).
//
// <ConfirmProvider> wraps LayoutRoutes only, as <ToastProvider> does. The
// ring screens (RingDisplay, RingOverlay) mount outside it: nobody answers a
// question on a TV. useConfirm() throws outside the provider.
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import ConfirmModal, { QUESTION_ARM_MS, SECOND_QUESTION_ARM_MS } from '../components/shared/ConfirmModal';
import { useI18n } from '../i18n';

/** A question with two answers: yes (`true`) or no (`false`). */
export interface ConfirmOptions {
  /** The title, in Title Case, and the name of the dialog. Default `t.common.confirm`. */
  title?: string;
  /** The question. A "\n" starts a new line. */
  message: string;
  /** The label of the confirm button: the action ("Delete", "Leave"). Default `t.common.confirm`. */
  confirmLabel?: string;
  /** The label of the cancel button. Default `t.common.cancel`. */
  cancelLabel?: string;
  /** The write removes or ends a record. ConfirmModal marks its form with
   *  `data-destructive`, shows a red confirm button and focuses Cancel. */
  destructive?: boolean;
  /** The first focus goes to Cancel, so Enter answers no. The confirm button
   *  keeps its look. For a question where no is the safe answer but the write
   *  does not remove a record (the over-limit questions of the weigh-in). */
  focusCancel?: boolean;
}

/** A question with a text field, in place of prompt(). */
export interface AskTextOptions {
  /** The title, in Title Case, and the name of the dialog. Default `t.common.confirm`. */
  title?: string;
  /** The question. With no `label`, it is the label of the text field. */
  message: string;
  /** The label of the text field. With a label, the message shows above the field. */
  label?: string;
  /** The text in the field when the question opens. */
  initialValue?: string;
  /** The label of the confirm button. Default `t.common.confirm`. */
  confirmLabel?: string;
}

export interface ConfirmAPI {
  /** Shows the question. Gives `true` for yes and `false` for no. */
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  /** Shows the question with a text field. Gives the text, or `null` for no.
   *  The confirm button is off while the field is empty, so the text is never empty. */
  askText: (options: AskTextOptions) => Promise<string | null>;
}

/** The functions of the provider. `page` is the path of the render of the
 *  consumer that asks: useConfirm() binds it. */
interface ConfirmContextValue {
  confirm: (options: ConfirmOptions, page: string) => Promise<boolean>;
  askText: (options: AskTextOptions, page: string) => Promise<string | null>;
}

type Question =
  | { id: number; kind: 'confirm'; options: ConfirmOptions; resolve: (answer: boolean) => void }
  | { id: number; kind: 'text'; options: AskTextOptions; resolve: (answer: string | null) => void };

/** The question on the screen, and the pause of its confirm button. */
interface Shown { question: Question; armMs: number }

/** Answer no: `false` to confirm(), `null` to askText(). */
function answerNo(q: Question): void {
  if (q.kind === 'confirm') q.resolve(false);
  else q.resolve(null);
}

/** True when two questions show the same text: a double tap asked twice.
 *  Two text questions are the same only with the same label and the same
 *  first text in the field. */
function sameQuestion(a: Question, b: Question): boolean {
  if (a.kind !== b.kind
    || a.options.title !== b.options.title
    || a.options.message !== b.options.message
    || a.options.confirmLabel !== b.options.confirmLabel) return false;
  if (a.kind === 'text' && b.kind === 'text') {
    return a.options.label === b.options.label && a.options.initialValue === b.options.initialValue;
  }
  return true;
}

const ConfirmContext = createContext<ConfirmContextValue | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const { t } = useI18n();
  const [shown, setShown] = useState<Shown | null>(null);
  const [text, setText] = useState('');
  // The refs are the source of the queue: two calls in one task must see
  // each other before the next render.
  const current = useRef<Question | null>(null);
  const waiting = useRef<Question[]>([]);
  const nextId = useRef(0);
  // performance.now() of the last answer. A monotonic clock: the wall clock
  // of a host can step back (rules/e2e.md).
  const answeredAt = useRef(Number.NEGATIVE_INFINITY);
  // The path of the page on the screen. The layout effect below moves it.
  const { pathname } = useLocation();
  const pagePath = useRef(pathname);

  const show = useCallback((q: Question | null) => {
    current.current = q;
    if (!q) { setShown(null); return; }
    const armMs = performance.now() - answeredAt.current < SECOND_QUESTION_ARM_MS ? SECOND_QUESTION_ARM_MS : QUESTION_ARM_MS;
    setText(q.kind === 'text' ? q.options.initialValue ?? '' : '');
    setShown({ question: q, armMs });
  }, []);

  const ask = useCallback((q: Question, page: string) => {
    // A question from a page that is gone: no at once, and nothing shows.
    if (page !== pagePath.current) { answerNo(q); return; }
    const open = current.current ? [current.current, ...waiting.current] : waiting.current;
    if (open.some(o => sameQuestion(o, q))) { answerNo(q); return; }
    if (current.current) waiting.current.push(q);
    else show(q);
  }, [show]);

  /** Answer the question on the screen, then show the next one. */
  const finish = useCallback((q: Question, yes: boolean, value: string) => {
    if (current.current !== q) return;
    answeredAt.current = performance.now();
    show(waiting.current.shift() ?? null);
    if (q.kind === 'confirm') q.resolve(yes);
    else q.resolve(yes ? value : null);
  }, [show]);

  /** Answer no to the open question and to each waiting question. */
  const cancelAll = useCallback(() => {
    const all = current.current ? [current.current, ...waiting.current] : [...waiting.current];
    waiting.current = [];
    if (current.current) show(null);
    all.forEach(answerNo);
  }, [show]);

  // A new page answers no: the question belongs to the page that asked it.
  // A layout effect, so the old question does not paint on the new page.
  // The search part of the URL (a tab, a filter) is not a new page.
  useLayoutEffect(() => {
    if (pagePath.current === pathname) return;
    pagePath.current = pathname;
    cancelAll();
  }, [pathname, cancelAll]);

  // The unmount of the provider answers no. No state update here.
  useEffect(() => () => {
    const all = current.current ? [current.current, ...waiting.current] : [...waiting.current];
    current.current = null;
    waiting.current = [];
    all.forEach(answerNo);
  }, []);

  const confirm = useCallback((options: ConfirmOptions, page: string) => new Promise<boolean>(resolve => {
    ask({ id: ++nextId.current, kind: 'confirm', options, resolve }, page);
  }), [ask]);

  const askText = useCallback((options: AskTextOptions, page: string) => new Promise<string | null>(resolve => {
    ask({ id: ++nextId.current, kind: 'text', options, resolve }, page);
  }), [ask]);

  // Stable callbacks give a stable value: a question does not render the
  // consumers again (the same rule as useToast, 2026-09-19).
  const api: ConfirmContextValue = useMemo(() => ({ confirm, askText }), [confirm, askText]);

  const q = shown?.question;
  return (
    <ConfirmContext.Provider value={api}>
      {children}
      {shown && q && (
        // A new key for each question: the pause of the confirm button starts again.
        <ConfirmModal
          key={q.id}
          title={q.options.title ?? t.common.confirm}
          message={q.kind === 'text' && !q.options.label ? undefined : q.options.message}
          confirmLabel={q.options.confirmLabel ?? t.common.confirm}
          cancelLabel={q.kind === 'confirm' ? q.options.cancelLabel : undefined}
          destructive={q.kind === 'confirm' && !!q.options.destructive}
          focusCancel={q.kind === 'confirm' && !!q.options.focusCancel}
          armMs={shown.armMs}
          input={q.kind === 'text' ? { label: q.options.label ?? q.options.message, value: text, onChange: setText } : undefined}
          onConfirm={() => finish(q, true, text)}
          onCancel={() => finish(q, false, '')}
        />
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmAPI {
  const ctx = useContext(ConfirmContext);
  // The path of this render. A handler keeps the functions of the render
  // that made it, so a late question of a page that is gone gets no at once.
  // The value changes only with the path.
  const { pathname } = useLocation();
  const bound = useMemo<ConfirmAPI | null>(() => ctx && {
    confirm: options => ctx.confirm(options, pathname),
    askText: options => ctx.askText(options, pathname),
  }, [ctx, pathname]);
  if (!bound) {
    throw new Error(
      'useConfirm() called outside <ConfirmProvider>. The provider wraps the ' +
      'routes of the normal layout (App.tsx). The ring screens (RingDisplay, ' +
      'RingOverlay) mount outside it on purpose: nobody answers a question on ' +
      'a TV. Ask the question in a component of the normal layout, or pass ' +
      'the action in as a prop. Do NOT widen <ConfirmProvider> to the ring screens.'
    );
  }
  return bound;
}
